import React, { useState } from "react";
import { motion } from "framer-motion";
import { NotebookPen, Brain, ShieldCheck, RotateCcw, ArrowRight, ShieldAlert } from "lucide-react";
import { api } from "@/lib/api";
import { AppStoreBadges } from "@/components/AppStoreBadges";

const CATEGORIES = ["Difficult conversations", "Receiving criticism", "Setting boundaries",
  "Managing anger", "Relationship conflict", "Work performance", "Parenting", "Confidence", "Other"];

const inputCls = "w-full rounded-xl border border-cardborder bg-cream px-4 py-3 text-sm text-ink placeholder:text-ink-muted/60 outline-none transition-all focus:border-sage focus:ring-2 focus:ring-sage/20 resize-none";

export function MomentsDemo() {
  const [what, setWhat] = useState("");
  const [felt, setFelt] = useState("");
  const [responded, setResponded] = useState("");
  const [category, setCategory] = useState("Difficult conversations");
  const [busy, setBusy] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [safety, setSafety] = useState(null);

  const reset = () => { setAnalysis(null); setSafety(null); };

  const submit = async () => {
    if (what.trim().length < 3 || busy) return;
    setBusy(true);
    setSafety(null);
    try {
      const res = await api("/demo/moment", {
        method: "POST",
        body: { what_happened: what.trim(), how_felt: felt.trim(), how_responded: responded.trim(), category },
      });
      if (res.safety) { setSafety(res.safety.message); return; }
      setAnalysis(res.analysis);
    } catch (e) {
      setSafety(null);
      setAnalysis({ _error: true });
    } finally {
      setBusy(false);
    }
  };

  return (
    <section id="moments" className="bg-cream">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-start">
        <div className="lg:sticky lg:top-24">
          <p className="text-xs font-mono uppercase tracking-[0.15em] text-sage-hover">Demo · Moments</p>
          <h2 className="mt-3 font-serif font-semibold tracking-tight text-3xl sm:text-4xl text-ink leading-tight">
            Capture a moment, get an instant recap
          </h2>
          <p className="mt-4 text-lg text-ink-muted leading-relaxed">
            Describe something that didn't go how you wanted. Next Time separates the facts from the story, shows what was in your control, and hands you a stronger line, all in a few seconds. No signup.
          </p>
          <ul className="mt-7 space-y-4">
            {[
              [Brain, "A calm, neutral recap of what happened"],
              [ShieldCheck, "What was genuinely within your control"],
              [NotebookPen, "A better response, and a line to actually say"],
            ].map(([Icon, t]) => (
              <li key={t} className="flex gap-3">
                <span className="mt-0.5 h-9 w-9 shrink-0 grid place-items-center rounded-xl bg-sage-light text-sage-hover"><Icon className="h-5 w-5" /></span>
                <span className="text-ink pt-1.5">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <div className="rounded-3xl border border-cardborder bg-cardbg p-6 sm:p-7 shadow-sm">
            {safety ? (
              <div className="rounded-xl bg-clay-light border border-clay/30 p-5 text-sm text-clay leading-relaxed flex gap-3">
                <ShieldAlert className="h-5 w-5 shrink-0" />
                <div>
                  <p>{safety}</p>
                  <button className="mt-4 font-semibold underline" onClick={reset} data-testid="moments-demo-safety-close">Back</button>
                </div>
              </div>
            ) : analysis ? (
              <Recap analysis={analysis} onReset={() => { reset(); }} />
            ) : (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">What happened? <span className="text-clay">*</span></label>
                  <textarea data-testid="moments-demo-what" value={what} onChange={(e) => setWhat(e.target.value)} rows={4} placeholder="e.g. My teammate took credit for my idea and I froze and said nothing." className={inputCls} />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-ink mb-1.5">How did you feel?</label>
                    <input data-testid="moments-demo-felt" value={felt} onChange={(e) => setFelt(e.target.value)} placeholder="Frustrated, small…" className={inputCls} />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-ink mb-1.5">How did you respond?</label>
                    <input data-testid="moments-demo-responded" value={responded} onChange={(e) => setResponded(e.target.value)} placeholder="Stayed silent…" className={inputCls} />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink mb-1.5">Category</label>
                  <select data-testid="moments-demo-category" value={category} onChange={(e) => setCategory(e.target.value)} className={inputCls + " cursor-pointer"}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <button
                  data-testid="moments-demo-submit"
                  onClick={submit}
                  disabled={busy || what.trim().length < 3}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-sage px-6 py-3 font-semibold text-white transition-all hover:bg-sage-hover disabled:opacity-50"
                >
                  {busy ? "Analyzing…" : "Get my recap"} {!busy && <ArrowRight className="h-4 w-4" />}
                </button>
                <p className="text-[11px] text-ink-muted text-center">Nothing is saved. This is a one-off demo analysis.</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Recap({ analysis, onReset }) {
  if (analysis._error) {
    return (
      <div className="text-center py-6">
        <p className="text-ink">The analysis is busy right now. Please try again in a moment.</p>
        <button onClick={onReset} className="mt-4 font-semibold text-sage-hover underline" data-testid="moments-demo-retry">Try again</button>
      </div>
    );
  }
  return (
    <div data-testid="moments-demo-recap">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-sage-light px-3 py-1 text-xs font-mono uppercase tracking-wider text-sage-hover">Your recap</span>
        <button onClick={onReset} className="p-2 text-ink-muted hover:text-ink" title="Try another" data-testid="moments-demo-reset"><RotateCcw className="h-4 w-4" /></button>
      </div>

      {analysis.summary && <p className="mt-4 text-ink leading-relaxed">{analysis.summary}</p>}

      <div className="mt-4 space-y-3">
        {analysis.likely_trigger && (
          <div className="rounded-xl bg-cream border border-cardborder p-3.5">
            <p className="text-xs font-mono uppercase tracking-wider text-ink-muted">Likely trigger</p>
            <p className="mt-1 text-ink text-sm">{analysis.likely_trigger}</p>
          </div>
        )}
        {analysis.within_control?.length > 0 && (
          <div className="rounded-xl bg-cream border border-cardborder p-3.5">
            <p className="text-xs font-mono uppercase tracking-wider text-ink-muted">Within your control</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {analysis.within_control.map((t, i) => (
                <span key={i} className="inline-flex items-center gap-1 rounded-full bg-sage-light px-2.5 py-1 text-xs text-sage-hover">✓ {t}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      {analysis.better_response && (
        <div className="mt-3 rounded-xl bg-sage-light border border-sage/30 p-4">
          <p className="text-xs font-mono uppercase tracking-wider text-sage-hover">A stronger response</p>
          <p className="mt-1.5 text-ink text-sm leading-relaxed">{analysis.better_response}</p>
          {analysis.suggested_wording && <p className="mt-2 text-ink italic">"{analysis.suggested_wording}"</p>}
        </div>
      )}

      <div className="mt-5 rounded-2xl bg-ink text-cream p-5 text-center">
        <p className="font-serif text-lg">That's one moment. The app remembers them all.</p>
        <p className="mt-1 text-sm text-cream/75">Next Time tracks your patterns over time and builds your playbook.</p>
        <div className="mt-4 flex justify-center"><AppStoreBadges dark /></div>
      </div>
    </div>
  );
}
